// components/portal/tables/marketSignals/MarketSignalsTable.tsx
'use client';

import React, { useCallback, useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { LeasingMarketSignalFilters } from '#types/types';
import type { InventoryEntityState } from '#types/types';
import { Table, TableColumnDef } from '../../../portal/Table';
import { Activity, Coins, FileText, Globe, MapPin, Ruler } from '@/components/ui/lucide-memo';

const translations = {
  en: {
    listingHeader: 'Listing / Headline',
    locationHeader: 'Location',
    surfaceHeader: 'Surface',
    rentHeader: 'Rent',
    sourceHeader: 'Source',
    statusHeader: 'Status',
    createdDate: 'Created Date',
    surfaceArea: 'Surface Area',
    rentAmount: 'Rent Amount',
  },
  fr: {
    listingHeader: 'Annonce / Titre',
    locationHeader: 'Localisation',
    surfaceHeader: 'Surface',
    rentHeader: 'Loyer',
    sourceHeader: 'Source',
    statusHeader: 'Statut',
    createdDate: 'Date de creation',
    surfaceArea: 'Surface',
    rentAmount: 'Montant du loyer',
  },
};

interface MarketSignalsTableProps {
  lockedFilters?: Partial<LeasingMarketSignalFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
}

export const MarketSignalsTable = React.memo(({
  lockedFilters,
  assetLedgerTitle = 'All signals',
  assetLedgerBreadcrumb = 'Inventory / Signals',
}: MarketSignalsTableProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'listing', icon: FileText, minWidth: 250, header: t.listingHeader },
    { id: 'location', icon: MapPin, minWidth: 180, header: t.locationHeader },
    { id: 'surface', icon: Ruler, minWidth: 100, header: t.surfaceHeader },
    { id: 'rent', icon: Coins, minWidth: 120, header: t.rentHeader },
    { id: 'source', icon: Globe, minWidth: 100, header: t.sourceHeader },
    { id: 'status', icon: Activity, minWidth: 100, header: t.statusHeader },
    { id: 'actions', minWidth: 100, header: '', align: 'right' },
  ], [t]);

  const sortOptions = useMemo(() => [
    { label: t.createdDate, field: 'createdAt' },
    { label: t.surfaceArea, field: 'areaSqm' },
    { label: t.rentAmount, field: 'rentAmount' },
  ], [t]);

  const renderRow = useCallback((
    signal: InventoryEntityState<'MarketSignal'>,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
  ) => (
    <div className="flex w-full flex-none flex-col">
      <EntityView
        type="MarketSignal"
        entityId={signal.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
      />
      {isExpanded && (
        <EntityView
          type="MarketSignal"
          entityId={signal.id}
          variant="page"
          pageHasEntityRowAbove
          className="h-auto border-b border-[#2A2A2A]"
        />
      )}
    </div>
  ), []);

  return (
    <Table
      entityType="MarketSignal"
      lockedFilters={lockedFilters}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      renderRow={renderRow}
    />
  );
});

MarketSignalsTable.displayName = 'MarketSignalsTable';


